import type { CoverageDetailDto, LlmNoteDto, ReclassificationDto } from "../api";
import { FieldHelp } from "./FieldHelp";
import { AsOfBadge } from "./AsOfBadge";
import { ConvictionBadge, ConvictionExplain } from "./ConvictionBadge";

/**
 * "Where this signal comes from" (ui_spec G6) -- the honest-modeling
 * centerpiece. Renders the coverage signal WITH its full provenance, not
 * just the number: contributing segments (+ weight), gaps (+ reason),
 * the resolved horizon, and the earnings-overlay state.
 *
 * DELIBERATELY DIFFERENT FROM THE OLDER MOCK (tickerpicker_ui_detail_
 * provenance.html): that mock predates the 2026-08-14 S10.13 amendment and
 * shows a "74% agree" confidence ring + per-segment percentages implying
 * within-slot agreement/dispersion math. The cross-segment blend that
 * actually produces a stock's signal NEVER computes that (G3's own
 * doctrine: shrink-to-equal weight, not agreement/dispersion -- that's
 * F6.6's within-slot concept). Per CLAUDE.md's working agreement ("when a
 * spec/mock and the current implementation disagree, the more recent,
 * more specific source wins"), this panel shows real per-segment WEIGHTS
 * (which coverage.py does compute) with no confidence number anywhere.
 */
export function ProvenancePanel({
  coverage,
  outputType,
  reclassifications,
  onSimulate,
  llmNote,
  showNotes,
}: {
  coverage: CoverageDetailDto;
  outputType: "return" | "volatility";
  reclassifications: ReclassificationDto[];
  onSimulate?: () => void;
  /** Deterministic, zero-LLM templated note for this signal -- see
   * app/factory/llm_notes.py. `null`/`undefined` renders nothing. */
  llmNote?: LlmNoteDto | null;
  /** Preview gate (2026-10-25): the panel still fetches/receives the note
   * either way, but only RENDERS it when this is true -- keeps the note
   * opt-in while it's reviewed, per the explicit "preview first, not the
   * default view" instruction, without needing a second API shape. */
  showNotes?: boolean;
}) {
  const { signal, signal_skill_weighted, skill_delta, scale_note, matched_horizon, contributing_segments,
          gaps, near_earnings, days_since_earnings,
          signal_as_of, secondary_horizons, conviction } = coverage;

  const fmt = (v: number) => (outputType === "volatility" ? v.toFixed(4) : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(2)}%`);
  const direction: "up" | "down" | "flat" | "none" =
    signal === null ? "none" : signal > 0 ? "up" : signal < 0 ? "down" : "flat";
  const valueText = signal === null ? "no signal" : fmt(signal);
  const skillValueText = signal_skill_weighted === null ? "no signal" : fmt(signal_skill_weighted);
  // DUAL-WEIGHT SIGNAL (2026-09-21): |delta| < 0.001 (0.1pp) reads as
  // "agree" -- shrinkage (lambda=0.3) keeps low-model-count horizons close
  // to equal by design, so a near-zero delta is the expected, honest
  // reading for most stocks, not a defect.
  const skillDiverges = skill_delta !== null && Math.abs(skill_delta) >= 0.001;

  const relevantReclass = reclassifications.filter((r) =>
    contributing_segments.some((c) => c.segment_key.startsWith(r.dimension + ":")) ||
    gaps.some((g) => g.segment_key.startsWith(r.dimension + ":")),
  );

  // TESTED-NO-SIGNAL, DISTINCT FROM A GAP (2026-09-25): a segment where a
  // real build attempt confirmed no forecastable signal (e.g. volatility
  // tiers) is a RESOLVED negative, not an omission -- rendering it under
  // "Gaps — didn't contribute, and why" would read as incomplete when it is
  // actually a measured, settled finding. Split by gap_type before the
  // Gaps section renders, so each gets its own honest framing.
  const realGaps = gaps.filter((g) => g.gap_type !== "tested_no_signal");
  const testedNoSignal = gaps.filter((g) => g.gap_type === "tested_no_signal");

  return (
    <div className="prov-panel">
      <div className="prov-head">
        <span>
          {outputType === "volatility" ? "Volatility (σ) — where this comes from" : "Return — where this comes from"}{" "}
          <FieldHelp term={outputType === "volatility" ? "rank_space_sigma" : "coverage"} />
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ConvictionBadge conviction={conviction} />
          {signal !== null ? <AsOfBadge date={signal_as_of} /> : <span className="prov-note">computed · as-of-T</span>}
        </div>
      </div>

      {/* DUAL-WEIGHT SIGNAL (2026-09-21): both weightings always shown side
         by side (Option A -- full transparency, neither hidden). Equal is
         the proven-safe baseline; skill-weighted is marked PRIMARY because
         it is the research-validated-better scheme where enough diverse
         models exist -- shrinkage keeps it close to equal everywhere else. */}
      <div className="weights-row">
        <div className="wcard">
          <div className="wlabel">Signal · equal-weight <FieldHelp term="cross_segment_blend" /></div>
          <div className={`wval ${direction === "up" ? "pos" : direction === "down" ? "neg" : "mut"}`}>{valueText}</div>
          <div className="wsub">
            {matched_horizon !== null ? `${matched_horizon}-day` : "no horizon"} · {contributing_segments.length} segment{contributing_segments.length === 1 ? "" : "s"} blended equally
          </div>
        </div>
        <div className="wcard primary">
          <div className="wlabel">Signal · skill-weighted <span className="tag">PRIMARY</span></div>
          <div className={`wval ${direction === "up" ? "pos" : direction === "down" ? "neg" : "mut"}`}>{skillValueText}</div>
          <div className="wsub">
            {matched_horizon !== null ? `${matched_horizon}-day` : "no horizon"} · shrunk IC-weight (λ=0.3)
          </div>
        </div>
        {outputType === "volatility" && signal !== null && <span className="rank-space-badge lg">rank-space</span>}
      </div>

      {signal !== null && signal_skill_weighted !== null && (() => {
        const withIc = contributing_segments.filter((c) => c.ic !== null);
        const top = withIc.length > 1 ? withIc.reduce((a, b) => (b.ic! > a.ic! ? b : a)) : null;
        const bottom = withIc.length > 1 ? withIc.reduce((a, b) => (b.ic! < a.ic! ? b : a)) : null;
        return skillDiverges ? (
          <div className="diverge-note">
            <b>Skill-weighting adjusts this {skill_delta! >= 0 ? "+" : ""}{fmt(skill_delta!)}.</b>{" "}
            {top && bottom && top.segment_key !== bottom.segment_key ? (
              <>Among this stock's {matched_horizon}-day segments, the higher-IC model
              ({top.segment_key}, IC {top.ic!.toFixed(2)}) carries more weight than the lower-IC one
              ({bottom.segment_key}, IC {bottom.ic!.toFixed(2)}) — pulling the signal toward its prediction.</>
            ) : (
              <>The contributing segments differ meaningfully in validated live skill (rank-IC), so
              skill-weighting shifts real weight away from a plain equal split.</>
            )}
          </div>
        ) : (
          <div className="agree-note">
            <b>Skill-weighting barely changes this ({skill_delta! >= 0 ? "+" : ""}{fmt(skill_delta!)}).</b>{" "}
            {contributing_segments.length <= 1
              ? "Only one segment contributes at this horizon -- there is nothing to weight between."
              : "This horizon's contributing segments carry similar validated skill, so shrunk IC-weighting stays close to equal — equal-weight was already a fine estimate here."}
          </div>
        );
      })()}

      {secondary_horizons.length > 0 && (
        <div className="prov-section">
          <div className="prov-section-label">Other horizons with signal <FieldHelp term="cross_horizon_conviction" /></div>
          {secondary_horizons.map((h) => {
            const hDir = h.signal > 0 ? "up" : h.signal < 0 ? "down" : "flat";
            const hValText = outputType === "volatility" ? h.signal.toFixed(4)
              : `${h.signal >= 0 ? "+" : ""}${(h.signal * 100).toFixed(2)}%`;
            return (
              <div className="prov-signal prov-signal-secondary" key={h.horizon}>
                <span className={`signal-dot ${hDir}`} />
                <div>
                  <div className={hDir === "up" ? "pos" : hDir === "down" ? "neg" : "mut"} style={{ fontWeight: 700 }}>
                    {hValText}
                  </div>
                  <div className="prov-note">
                    {h.horizon}-day horizon ·{" "}
                    {h.contributing_segments.map((c) => c.segment_key).join(", ")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConvictionExplain
        conviction={conviction}
        primaryHorizon={matched_horizon}
        primarySignal={signal}
        secondary={secondary_horizons.map((h) => ({ horizon: h.horizon, signal: h.signal }))}
      />

      <div className="prov-scale-note">{scale_note}</div>

      <div className="prov-no-confidence">
        No confidence shown here by design: the <FieldHelp term="cross_segment_blend" />cross-segment blend never reports agreement/dispersion (that's a
        within-slot ensemble concept, not this one) — see the footnote below.
      </div>

      {contributing_segments.length > 0 && (
        <div className="prov-section">
          <div className="prov-section-label">Contributing segments</div>
          {contributing_segments.map((c) => {
            const dim = c.segment_key.split(":")[0];
            return (
              <div className="prov-src" key={c.segment_key}>
                <span className="dim-chip">{dim}</span>
                <span className="prov-src-name">{c.segment_key}</span>
                <span className="prov-src-model">{c.model_id}</span>
                {c.ic !== null && <span className="mut" style={{ fontSize: "0.72rem" }} title="This model's live trailing rank-IC">IC {c.ic.toFixed(3)}</span>}
                <div className="wbar" title="equal-weight share">
                  <div className="f" style={{ width: `${Math.round(c.weight * 100)}%` }} />
                </div>
                <span className="prov-w">{Math.round(c.weight * 100)}%</span>
                <div className="wbar" title="skill-weighted share" style={{ opacity: 0.75 }}>
                  <div className="f" style={{ width: `${Math.round(c.weight_skill * 100)}%`, background: "var(--accent)" }} />
                </div>
                <span className="prov-w">{Math.round(c.weight_skill * 100)}% skill</span>
              </div>
            );
          })}
        </div>
      )}

      {testedNoSignal.length > 0 && (
        <div className="prov-section">
          <div className="prov-section-label">Tested — no directional signal <FieldHelp term="tested_no_signal" /></div>
          {testedNoSignal.map((g) => (
            <div className="prov-tested" key={g.segment_key}>
              <span className="dim-chip tested">{g.segment_key.split(":")[0]}</span>
              <span className="prov-src-name">{g.segment_key}</span>
              <span className="prov-tested-reason">{g.reason}</span>
            </div>
          ))}
        </div>
      )}

      {realGaps.length > 0 && (
        <div className="prov-section">
          <div className="prov-section-label">Gaps — didn't contribute, and why <FieldHelp term="coverage_gap" /></div>
          {realGaps.map((g) => (
            <div className="prov-gap" key={g.segment_key}>
              <span className="dim-chip mut">{g.segment_key.split(":")[0]}</span>
              <span className="prov-src-name mut">{g.segment_key}</span>
              <span className="prov-gap-reason">{g.reason}</span>
            </div>
          ))}
        </div>
      )}

      {contributing_segments.length === 0 && gaps.length === 0 && (
        <div className="prov-section prov-empty">This stock has no active segment memberships at all — no signal is possible.</div>
      )}

      {near_earnings && (
        <div className="prov-earn">
          ⚡ Near a recent earnings announcement ({days_since_earnings} day{days_since_earnings === 1 ? "" : "s"} ago)
          — earnings overlay armed.
        </div>
      )}

      {relevantReclass.length > 0 ? (
        <div className="prov-recl">
          ↔ Recent reclassification:{" "}
          {relevantReclass.map((r, i) => (
            <span key={i}>
              {r.dimension} {r.from_segment ?? "(new)"} → {r.to_segment} on {r.changed_on}
              {r.consequence ? ` (${r.consequence})` : ""}
              {i < relevantReclass.length - 1 ? "; " : ""}
            </span>
          ))}
        </div>
      ) : (
        <div className="prov-recl mut">↔ No recent reclassification affecting this signal's segments.</div>
      )}

      {showNotes && llmNote?.text && (
        <div className="prov-llm-note">
          <div className="prov-llm-note-label">
            <span>Generated from platform figures</span>
            {llmNote.register && <span className="prov-llm-note-register">{llmNote.register}</span>}
          </div>
          <div className="prov-llm-note-text">{llmNote.text}</div>
        </div>
      )}

      <div className="prov-links">
        <button className="lk" disabled title="Coverage resolver (admin) not built yet -- Phase 9">
          Coverage detail →
        </button>
        <button className="lk" onClick={onSimulate} disabled={!onSimulate}>
          Simulate this →
        </button>
      </div>
      <div className="prov-footnote">
        Read-only. Signal is auto-assembled from the segment models this stock belongs to — nothing stored per-stock.
        To hand-tune, use the Simulator (not yet built).
      </div>
    </div>
  );
}
